/* ═══════════════════════════════════════════════════════════════
   useCollabDoc — one shared Yjs document over the /collab socket
   (docs/SPEC_WORKFLOW.md §5). Hocuspocus provider with its built-in
   reconnect backoff; edits made while offline queue in the Y.Doc and merge
   on reconnect (that is Yjs, not code here).

   The Yjs / Hocuspocus surface is untyped in places (awareness states,
   close events) — every read of it goes through a narrow typed accessor
   here, so nothing `any` leaks into the hooks that consume this.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import * as Y from 'yjs';
import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import { getAuthToken } from '../services/authService';
import { resolveSocketUrl } from '../services/liveSync';
import {
  CLOSE_DOCUMENT_RESET,
  CLOSE_DRAFT_DISCARDED,
  cursorSlot,
  nextLiveBlock,
  peersFromAwareness,
  type CollabPeer,
  type CollabStatus,
  type LiveBlock,
} from './collabState';

export interface CollabUser {
  name: string;
  /** `var(--collab-cursor-<slot>)` — a token reference, never a raw colour. */
  color: string;
  slot: number;
}

export interface CollabDoc {
  ydoc: Y.Doc;
  provider: HocuspocusProvider;
  /** This window's seat — the server names ONE seat as the seeder. */
  seat: string;
  user: CollabUser;
  status: CollabStatus;
  /** The first full sync with the server has landed. */
  synced: boolean;
  unsyncedChanges: number;
  peers: CollabPeer[];
  /** The server refused this document (no view right / wrong firm). */
  denied: string | null;
  /** The draft row was deleted under us (discard / publish): the socket is
   *  closed for good and the caller should leave. */
  discarded: boolean;
  /** The row's JSON changed outside the editor (an accept, import, revert...):
   *  the socket is closed like a discard (`discarded` is also true) but the
   *  caller should say "refreshed" and re-open, not "closed by a teammate". */
  reset: boolean;
  /** The server refuses this socket in a way no retry fixes (4001 auth,
   *  4003 origin, 4400 bad request, 4429 too many, or a per-document
   *  denial). Cleared by the next full sync — the provider keeps retrying. */
  blocked: LiveBlock | null;
}

function newSeat(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `seat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function toStatus(s: WebSocketStatus): CollabStatus {
  if (s === WebSocketStatus.Connected) return 'connected';
  if (s === WebSocketStatus.Connecting) return 'connecting';
  return 'offline';
}

function closeCodeOf(ev: unknown): number | null {
  const e = (ev as { event?: { code?: unknown } } | null)?.event;
  return typeof e?.code === 'number' ? e.code : null;
}

function isDiscardMessage(payload: string): boolean {
  try {
    const msg = JSON.parse(payload) as { type?: unknown };
    return msg?.type === 'draft-discarded';
  } catch {
    return false;
  }
}

function isResetMessage(payload: string): boolean {
  try {
    const msg = JSON.parse(payload) as { type?: unknown };
    return msg?.type === 'document-reset';
  } catch {
    return false;
  }
}

/**
 * @param docName  `master:<id>` / `project:<id>`, or null to stay idle.
 * @param userName shown on this user's cursor and in everyone's peer chips.
 */
export function useCollabDoc(
  docName: string | null,
  userName: string,
  opts?: {
    /** false = connected but invisible: no name in anyone's peer chips (the
     *  View mode watching a draft, SPEC_WORKFLOW §4). Default true. */
    announce?: boolean;
    /** Bump to drop this socket and open a fresh one (after a publish). */
    epoch?: number;
  }
): CollabDoc | null {
  const [doc, setDoc] = React.useState<CollabDoc | null>(null);
  const announce = opts?.announce !== false;
  const epoch = opts?.epoch ?? 0;
  const announceRef = React.useRef(announce);
  announceRef.current = announce;

  React.useEffect(() => {
    if (!docName) {
      setDoc(null);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setDoc(null);
      return;
    }
    const ydoc = new Y.Doc();
    const seat = newSeat();
    const slot = cursorSlot(userName);
    const user: CollabUser = { name: userName, color: `var(--collab-cursor-${slot})`, slot };
    let disposed = false;

    const provider = new HocuspocusProvider({
      url: resolveSocketUrl('/collab'),
      name: docName,
      document: ydoc,
      // The upgrade is authenticated from the query string (same rule as
      // /ws); the provider's own auth message carries the same JWT for the
      // per-document check.
      parameters: { token, seat },
      token,
      // Another window of this app on this machine is a separate seat with
      // its own cursor — not a silent same-tab mirror.
      broadcast: false,
    });

    let state: CollabDoc = {
      ydoc,
      provider,
      seat,
      user,
      status: 'connecting',
      synced: false,
      unsyncedChanges: 0,
      peers: [],
      denied: null,
      discarded: false,
      reset: false,
      blocked: null,
    };
    const publish = (patch: Partial<CollabDoc>) => {
      if (disposed) return;
      state = { ...state, ...patch };
      setDoc(state);
    };
    publish({});

    const endForGood = () => {
      if (state.discarded) return;
      publish({ discarded: true, status: 'offline' });
      // No reconnect: the document this socket was editing no longer exists.
      provider.disconnect();
    };

    const endForReset = () => {
      if (state.discarded) return;
      publish({ discarded: true, reset: true, status: 'offline' });
      provider.disconnect();
    };

    provider.awareness?.setLocalStateField('user', announceRef.current ? user : null);

    provider.on('status', ({ status }: { status: WebSocketStatus }) => {
      if (!state.discarded) publish({ status: toStatus(status) });
    });
    provider.on('synced', ({ state: s }: { state: boolean }) =>
      publish(
        s ? { synced: s, blocked: nextLiveBlock(state.blocked, { kind: 'synced' }) } : { synced: s }
      )
    );
    provider.on('unsyncedChanges', (n: number) => publish({ unsyncedChanges: n }));
    provider.on('authenticationFailed', ({ reason }: { reason: string }) =>
      publish({
        denied: reason || 'Permission denied',
        status: 'offline',
        blocked: nextLiveBlock(state.blocked, { kind: 'denied' }),
      })
    );
    provider.on('close', (ev: unknown) => {
      const code = closeCodeOf(ev);
      if (code === CLOSE_DRAFT_DISCARDED) endForGood();
      else if (code === CLOSE_DOCUMENT_RESET) endForReset();
      else {
        const blocked = nextLiveBlock(state.blocked, { kind: 'close', code });
        if (blocked !== state.blocked) publish({ blocked, status: 'offline' });
      }
    });
    provider.on('stateless', ({ payload }: { payload: string }) => {
      if (isDiscardMessage(payload)) endForGood();
      else if (isResetMessage(payload)) endForReset();
    });
    const onAwareness = () => {
      const aw = provider.awareness;
      if (!aw) return;
      publish({ peers: peersFromAwareness(aw.getStates().entries(), ydoc.clientID, userName) });
    };
    provider.awareness?.on('change', onAwareness);

    return () => {
      disposed = true;
      provider.awareness?.off('change', onAwareness);
      provider.destroy();
      ydoc.destroy();
    };
  }, [docName, userName, epoch]);

  // Entering / leaving the draft flips presence without a reconnect.
  React.useEffect(() => {
    if (!doc) return;
    doc.provider.awareness?.setLocalStateField('user', announce ? doc.user : null);
  }, [doc?.provider, doc?.user, announce]); // eslint-disable-line react-hooks/exhaustive-deps

  return doc;
}
